/**
 * Enth Projects - Favourites System
 * Backed by Swym Wishlist Plus. Click handlers and rendering are ours;
 * Swym handles persistence, sync across devices, and admin visibility.
 */

class EnthFavourites {
  constructor() {
    this.favourites = new Set(); // product IDs (as strings) currently in the Swym wishlist
    this.swymReady = false;
    this.init();
  }

  init() {
    this.bindClicks();
    this.observeSwymEmptyState();
    this.waitForSwym();
  }

  /**
   * Swym hard-codes both its copy and its styling inside its own app. We
   * override them into Enth's voice/look once the elements appear — for the
   * empty wishlist (title, description, "notice" line, "keep browsing" button)
   * AND the logged-out-with-items "login to save" state (heading, sub-line),
   * AND the populated product-card grid (split the one-line "Artist, Title,
   * Year" credit onto two lines, match the site line-height, left-align the
   * Add to Cart button, drop the grey image backdrop).
   * The login button and the sign-up link are left untouched so they stay
   * functional. Copy overrides are idempotent (text comparison or a data flag);
   * styling is applied inline with !important because Swym's app CSS loads after
   * the theme and would otherwise win the source-order tie. The MutationObserver
   * re-runs all of this cheaply on every Swym re-render. Selectors match Swym's
   * live markup and use querySelectorAll so the drawer and full page are hit.
   */
  applySwymEmptyState() {
    document.querySelectorAll('.swym-storefront-layout-empty-wishlist-title').forEach((el) => {
      if (el.textContent.trim() !== 'Your Selection') el.textContent = 'Your Selection';
    });

    document.querySelectorAll('.swym-storefront-layout-empty-wishlist-description').forEach((el) => {
      if (el.dataset.enthCopy === 'true') return;
      el.textContent = 'A space to keep track of works that resonate with you as you explore Enth Projects.';
      el.dataset.enthCopy = 'true';
    });

    document.querySelectorAll('.swym-storefront-layout-empty-state-notice-text').forEach((el) => {
      if (el.dataset.enthCopy === 'true') return;
      el.textContent = 'Login or create an account to save your selections permanently.';
      el.dataset.enthCopy = 'true';
    });

    document.querySelectorAll('.swym-storefront-layout-empty-state-browse-cta').forEach((el) => {
      if (el.textContent.trim() !== 'Continue Exploring') el.textContent = 'Continue Exploring';
    });

    // Drop Swym's "— it takes a minute" tail from the sign-up link so the line
    // reads simply "No account? Sign up".
    document.querySelectorAll('.swym-storefront-layout-empty-state-signup-link').forEach((el) => {
      if (el.textContent.trim() !== 'Sign up') el.textContent = 'Sign up';
    });

    // Logged-out-with-items "login to save" state: rewrite Swym's heading and
    // sub-line into Enth's voice. We replace only the text node so any inline
    // icon inside the element survives. The login button already reads
    // "Login to Save →", so it's left untouched.
    document.querySelectorAll('.swym-storefront-layout-login-heading').forEach((el) => {
      if (el.dataset.enthCopy === 'true') return;
      let replaced = false;
      el.childNodes.forEach((n) => {
        if (n.nodeType === 3 && n.textContent.trim()) {
          n.textContent = 'Works you’ve selected are saved here.';
          replaced = true;
        }
      });
      if (replaced) el.dataset.enthCopy = 'true';
    });

    document.querySelectorAll('.swym-storefront-layout-login-description').forEach((el) => {
      if (el.dataset.enthCopy === 'true') return;
      let replaced = false;
      el.childNodes.forEach((n) => {
        if (n.nodeType === 3 && n.textContent.trim()) {
          n.textContent = 'Create an account to keep your selections accessible across future visits and devices.';
          replaced = true;
        }
      });
      if (replaced) el.dataset.enthCopy = 'true';
    });

    // Product cards: Swym shows the whole product title — "Artist, Title, Year"
    // — as one unbroken line. The theme renders artist and title/year on
    // separate lines everywhere else (card-artwork.liquid splits on ', '), so
    // mirror that here: keep the artist on line 1 and drop the title + year to
    // line 2. Split on the FIRST ', ' only, so a title containing commas stays
    // intact on line 2. Guarded so an already-split node is never re-split.
    document.querySelectorAll('.swym-storefront-layout-grid-item-title').forEach((el) => {
      if (el.dataset.enthSplit === 'true') return;
      const raw = el.textContent.trim();
      const sep = raw.indexOf(', ');
      if (sep === -1) return; // no comma — leave single-line titles as-is
      el.textContent = '';
      el.appendChild(document.createTextNode(raw.slice(0, sep)));
      el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(raw.slice(sep + 2)));
      el.dataset.enthSplit = 'true';
    });

    // Styling overrides — applied the same way as the copy above (inline, via
    // the observer). Swym's app CSS loads AFTER the theme and uses ID-scoped
    // !important rules, so CSS overrides lose the source-order tie no matter how
    // we scope them. Inline styles sit at the top of the cascade, so this is
    // bulletproof against both load order and Swym re-renders.

    // Hide Swym's header (its grey divider is a box-shadow on the same node),
    // the yellow notice box (it just repeats the button messaging), and the
    // empty-state divider line (both desktop and mobile).
    document.querySelectorAll(
      '.swym-storefront-layout-header, .swym-storefront-layout-empty-state-notice, .swym-storefront-layout-empty-state-divider'
    ).forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });

    // Left-align the empty state and drop Swym's lateral padding so the content
    // sits flush to the drawer edge — same on desktop and mobile (the wishlist is
    // a side drawer in both). Set inline so it beats Swym's later-loading
    // !important rules; reapplied each re-render.
    document.querySelectorAll('.swym-storefront-layout-empty-wishlist-container').forEach((el) => {
      el.style.setProperty('text-align', 'left', 'important');
      el.style.setProperty('padding-left', '0px', 'important');
      el.style.setProperty('padding-right', '0px', 'important');
    });

    // Unify title + sub-line: one size, all black (#222), left-aligned.
    document.querySelectorAll(
      '.swym-storefront-layout-empty-wishlist-title, .swym-storefront-layout-empty-wishlist-description'
    ).forEach((el) => {
      el.style.setProperty('font-size', '16px', 'important');
      el.style.setProperty('font-weight', '500', 'important');
      el.style.setProperty('color', 'rgb(34, 34, 34)', 'important');
      el.style.setProperty('text-align', 'left', 'important');
    });

    // Petit espace sous le titre pour le détacher de la sous-ligne.
    document.querySelectorAll('.swym-storefront-layout-empty-wishlist-title').forEach((el) => {
      el.style.setProperty('margin-bottom', '5px', 'important');
    });

    // Espace sous la sous-ligne pour la détacher des CTA.
    document.querySelectorAll('.swym-storefront-layout-empty-wishlist-description').forEach((el) => {
      el.style.setProperty('margin-bottom', '30px', 'important');
    });

    // CTA labels match the rest; both buttons get an identical 1px border.
    document.querySelectorAll('.swym-storefront-layout-empty-state-cta-button').forEach((el) => {
      el.style.setProperty('font-size', '16px', 'important');
      el.style.setProperty('font-weight', '500', 'important');
      el.style.setProperty('color', 'rgb(34, 34, 34)', 'important');
      el.style.setProperty('border', '1px solid rgb(28, 28, 28)', 'important');
    });

    // "Sign up" link en gris discret.
    document.querySelectorAll('.swym-storefront-layout-empty-state-signup-link').forEach((el) => {
      el.style.setProperty('color', '#9B9B9B', 'important');
    });

    // Remove the grey backdrop around each product image (Swym gives the image
    // container a light-grey background, which reads as a grey line/box around
    // the shot) so the artwork sits on white.
    document.querySelectorAll('.swym-storefront-layout-grid-item-image-container').forEach((el) => {
      el.style.setProperty('background-color', 'transparent', 'important');
    });

    // Match the credit line-height to the rest of the site — .card-artwork__title-year
    // uses 1.2 (--enth-line-height-body); Swym defaults to a looser ~1.5.
    document.querySelectorAll('.swym-storefront-layout-grid-item-title').forEach((el) => {
      el.style.setProperty('line-height', '1.2', 'important');
    });

    // Left-align the Add to Cart button. Swym centres it by stretching it to the
    // full card width; shrink it to its label and pull it to the left edge.
    document.querySelectorAll('.swym-storefront-layout-grid-item-action-container').forEach((el) => {
      el.style.setProperty('justify-content', 'flex-start', 'important');
    });
    document.querySelectorAll('.swym-storefront-layout-grid-item-add-to-cart-button').forEach((el) => {
      el.style.setProperty('justify-content', 'flex-start', 'important');
      el.style.setProperty('text-align', 'left', 'important');
      el.style.setProperty('width', 'auto', 'important');
    });

    // Pousser tout le bloc de la liste (état rempli) un peu vers le bas — il
    // colle trop au haut du drawer.
    document.querySelectorAll('.swym-storefront-layout-default-list-container').forEach((el) => {
      el.style.setProperty('margin-top', '60px', 'important');
    });
  }

  observeSwymEmptyState() {
    this.applySwymEmptyState();
    const observer = new MutationObserver(() => this.applySwymEmptyState());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  waitForSwym(attempts = 0) {
    if (typeof window._swat !== 'undefined' && typeof window._swat.fetch === 'function') {
      this.swymReady = true;
      this.loadFromSwym();
      return;
    }
    if (attempts > 100) return; // ~10s max
    setTimeout(() => this.waitForSwym(attempts + 1), 100);
  }

  loadFromSwym() {
    window._swat.fetch(
      (items) => {
        this.favourites = new Set((items || []).map((item) => String(item.empi)));
        this.updateUI();
        this.renderFavouritesPage();
      },
      (err) => console.warn('Swym fetch failed', err)
    );
  }

  has(productId) {
    return this.favourites.has(String(productId));
  }

  bindClicks() {
    document.addEventListener('click', (e) => {
      // Header "Wishlist" links open Swym's side drawer instead of navigating
      // to the full /pages/swym-wishlist page.
      const launcher = e.target.closest('[data-swym-launcher]');
      if (launcher) {
        e.preventDefault();
        this.openWishlistDrawer();
        return;
      }

      const btn = e.target.closest('[data-favourite-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      this.toggle(btn);
    });
  }

  /**
   * Open Swym's storefront-layout wishlist drawer. Swym renders the wishlist as a
   * right-side drawer (settings_data.json storefront_layout_*) and opens it when
   * the URL hash becomes '#swym-list' — its drawer registers a `hashchange`
   * listener (checkForSwymDrawerHash) and opens when the hash matches. Swym's
   * canonical opener `_swat.ui.open()` is overridden by the wishlist app embed to
   * set exactly that hash, so we prefer it (future-proof if the hash ever
   * changes) and fall back to setting the hash directly if Swym hasn't
   * initialised yet. On close Swym clears the hash via history.replaceState, so
   * re-clicking always re-fires the hashchange.
   */
  openWishlistDrawer() {
    if (window._swat && window._swat.ui && typeof window._swat.ui.open === 'function') {
      window._swat.ui.open();
    } else {
      window.location.hash = '#swym-list';
    }
  }

  toggle(btn) {
    if (!this.swymReady) {
      this.showToast('Favourites are still loading…');
      return;
    }
    const productId = btn.getAttribute('data-product-id');
    const productData = this.readProductData(btn);
    const isCurrentlyFavourited = this.has(productId);

    // Optimistic UI update
    if (isCurrentlyFavourited) {
      this.favourites.delete(String(productId));
    } else {
      this.favourites.add(String(productId));
    }
    this.updateUI();

    const onError = (err) => {
      console.warn('Swym call failed', err);
      // Roll back optimistic update
      if (isCurrentlyFavourited) {
        this.favourites.add(String(productId));
      } else {
        this.favourites.delete(String(productId));
      }
      this.updateUI();
      this.showToast('Could not update favourites');
    };

    if (isCurrentlyFavourited) {
      window._swat.removeFromWishList(
        productData,
        () => this.showToast('Removed from favourites'),
        onError
      );
    } else {
      window._swat.addToWishList(
        productData,
        () => {
          this.showToast('Added to favourites');
          // Chosen UX: open the wishlist drawer only when ADDING (not on remove),
          // to confirm the save by showing the item in context. Opened from the
          // success callback so the item is persisted in Swym before the drawer
          // fetches and renders, otherwise it could open without the new item.
          this.openWishlistDrawer();
        },
        onError
      );
    }
  }

  readProductData(btn) {
    return {
      empi: parseInt(btn.getAttribute('data-product-id'), 10),
      epi: parseInt(btn.getAttribute('data-variant-id'), 10),
      du: btn.getAttribute('data-product-url') || window.location.href,
      dt: btn.getAttribute('data-product-title') || document.title,
      iu: btn.getAttribute('data-product-image') || '',
      pr: parseFloat(btn.getAttribute('data-product-price')) || 0,
    };
  }

  updateUI() {
    document.querySelectorAll('[data-favourite-toggle]').forEach((btn) => {
      const productId = btn.getAttribute('data-product-id');
      const active = this.has(productId);
      btn.classList.toggle('is-active', active);
      const baseLabel = active ? 'Remove from favourites' : 'Add to favourites';
      btn.setAttribute('aria-label', baseLabel);
    });

    const countEl = document.querySelector('[data-favourites-count]');
    if (countEl) {
      countEl.textContent = this.favourites.size;
      countEl.style.display = this.favourites.size > 0 ? '' : 'none';
    }
  }

  showToast(message) {
    let container = document.getElementById('enth-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'enth-toast-container';
      document.body.appendChild(container);
    }
    const toast = document.createElement('div');
    toast.className = 'enth-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;
    container.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      setTimeout(() => toast.remove(), 600);
    }, 2000);
  }

  renderFavouritesPage() {
    const container = document.querySelector('[data-favourites-grid]');
    if (!container) return;

    if (!this.swymReady) {
      container.innerHTML = '<p style="color: var(--enth-color-secondary); font-size: 1.4rem;">Loading favourites…</p>';
      return;
    }

    window._swat.fetch(
      (items) => {
        if (!items || items.length === 0) {
          container.innerHTML = '<p style="color: var(--enth-color-secondary); font-size: 1.4rem;">No favourites yet.</p>';
          return;
        }
        container.innerHTML = items.map((item) => this.cardHTML(item)).join('');
      },
      () => {
        container.innerHTML = '<p style="color: var(--enth-color-secondary); font-size: 1.4rem;">Could not load favourites.</p>';
      }
    );
  }

  cardHTML(item) {
    const title = item.dt || '';
    const url = item.du || '#';
    const image = item.iu || '';
    const price = typeof item.pr === 'number' ? item.pr.toLocaleString(undefined, { style: 'currency', currency: item.ccode || 'EUR' }) : '';
    const safeTitle = title.replace(/"/g, '&quot;');
    return `
      <div class="card-artwork" data-product-id="${item.empi}">
        <a href="${url}" class="card-artwork__link">
          <div class="card-artwork__image-wrapper">
            ${image ? `<img src="${image}" alt="${safeTitle}" loading="lazy" class="card-artwork__image">` : ''}
          </div>
          <div class="card-artwork__info">
            <p class="card-artwork__title-year">${safeTitle}</p>
            ${price ? `<p class="card-artwork__price">${price}</p>` : ''}
          </div>
        </a>
        <button
          class="card-artwork__favourite is-active"
          data-favourite-toggle
          data-product-id="${item.empi}"
          data-variant-id="${item.epi}"
          data-product-title="${safeTitle}"
          data-product-url="${url}"
          data-product-image="${image}"
          data-product-price="${item.pr || 0}"
          aria-label="Remove from favourites"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <line x1="12" y1="2" x2="12" y2="22" stroke="currentColor" stroke-width="0.5"/>
            <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="0.5"/>
            <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" stroke-width="0.5"/>
            <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" stroke-width="0.5"/>
          </svg>
        </button>
      </div>
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.enthFavourites = new EnthFavourites();
  initAccountMenus();
});

// Account dropdown: hover (CSS) on desktop, tap-to-toggle on touch / mobile.
// The trigger is href="#", so we preventDefault and toggle .is-open + aria.
function initAccountMenus() {
  const menus = document.querySelectorAll('[data-account-menu]');
  if (!menus.length) return;

  menus.forEach((menu) => {
    const trigger = menu.querySelector('.enth-account-launcher-group__trigger');
    if (!trigger) return;
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const open = menu.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  document.addEventListener('click', (e) => {
    menus.forEach((menu) => {
      if (menu.contains(e.target)) return;
      menu.classList.remove('is-open');
      const trigger = menu.querySelector('.enth-account-launcher-group__trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    menus.forEach((menu) => {
      menu.classList.remove('is-open');
      const trigger = menu.querySelector('.enth-account-launcher-group__trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  });
}
